// hooks/useFeedActionSocket.js

import { useEffect, useCallback } from "react";

/* =========================================================
   HELPER — update every feed state slice atomically
========================================================= */
const updateAllFeeds = (
  updater,
  { setFeedPosts, setGlobalLivePosts, setInteractionPosts }
) => {
  setFeedPosts?.(updater);
  setGlobalLivePosts?.(updater);
  setInteractionPosts?.(updater);
};

/* =========================================================
   HOOK
========================================================= */
export default function useFeedActionSocket(
  socket,
  {
    chatId,

    setFeedPosts,
    setGlobalLivePosts,
    setInteractionPosts,

    setComments,

    setFeedError, // optional — receives { message } from backend "feed-error"
  }
) {
  // Stable reference so callbacks below don't re-create on every render
  const feedSetters = { setFeedPosts, setGlobalLivePosts, setInteractionPosts };

  /* =========================================================
     🗑️ DELETE POST (EMITTER)
  ========================================================= */
  const deletePost = useCallback(
    ({ postId }) => {
      if (!socket || !postId || !chatId) return;
      socket.emit("feed:delete-post", { postId, chatId });
    },
    [socket, chatId]
  );

  /* =========================================================
     🗑️ DELETE COMMENT (EMITTER)
  ========================================================= */
  const deleteComment = useCallback(
    ({ commentId, postId }) => {
      if (!socket || !commentId || !chatId) return;
      socket.emit("feed:delete-comment", { commentId, postId, chatId });
    },
    [socket, chatId]
  );

  /* =========================================================
     ✏️ EDIT POST (EMITTER)
  ========================================================= */
  const editPost = useCallback(
    ({ postId, text }) => {
      if (!socket || !postId || !chatId) return;
      socket.emit("feed:edit-post", { postId, chatId, text });
    },
    [socket, chatId]
  );

  /* =========================================================
     ✏️ EDIT COMMENT (EMITTER)
  ========================================================= */
  const editComment = useCallback(
    ({ commentId, text }) => {
      if (!socket || !commentId || !chatId) return;
      socket.emit("feed:edit-comment", { commentId, chatId, text });
    },
    [socket, chatId]
  );

  /* =========================================================
     🚩 REPORT POST (EMITTER)
  ========================================================= */
  const reportPost = useCallback(
    ({ postId, reason }) => {
      if (!socket || !postId) return;
      socket.emit("feed-report-post", { postId, reason });
    },
    [socket]
  );

  /* =========================================================
     🚩 REPORT COMMENT (EMITTER)
  ========================================================= */
  const reportComment = useCallback(
    ({ commentId, reason }) => {
      if (!socket || !commentId) return;
      socket.emit("feed-report-comment", { commentId, reason });
    },
    [socket]
  );

  /* =========================================================
     🗑️ POST DELETED (LISTENER)
  ========================================================= */
  const handlePostDeleted = useCallback(
    ({ postId }) => {
      if (!postId) return;

      updateAllFeeds(
        (prev = []) =>
          prev.filter((p) => String(p.postId) !== String(postId)),
        feedSetters
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFeedPosts, setGlobalLivePosts, setInteractionPosts]
  );

  /* =========================================================
     ✏️ POST EDITED (LISTENER)
  ========================================================= */
  const handlePostEdited = useCallback(
    ({ postId, text, isEdited, updatedAt }) => {
      if (!postId) return;

      updateAllFeeds(
        (prev = []) =>
          prev.map((p) =>
            String(p.postId) === String(postId)
              ? {
                  ...p,
                  text,
                  isEdited: Boolean(isEdited),
                  updatedAt,
                }
              : p
          ),
        feedSetters
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFeedPosts, setGlobalLivePosts, setInteractionPosts]
  );

  /* =========================================================
     🗑️ COMMENT DELETED (LISTENER)
  ========================================================= */
  const handleCommentDeleted = useCallback(
    ({ commentId, postId }) => {
      if (!commentId) return;

      // Permanently remove the comment from local state
      setComments((prev = []) =>
        prev.filter((c) => String(c.commentId) !== String(commentId))
      );

      // Update post comment counter locally
      if (postId) {
        updateAllFeeds(
          (prev = []) =>
            prev.map((p) =>
              String(p.postId) === String(postId)
                ? {
                    ...p,
                    commentsCount: Math.max(0, (p.commentsCount || 0) - 1),
                  }
                : p
            ),
          feedSetters
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setComments, setFeedPosts, setGlobalLivePosts, setInteractionPosts]
  );

  /* =========================================================
     ✏️ COMMENT EDITED (LISTENER)
  ========================================================= */
  const handleCommentEdited = useCallback(
    ({ commentId, text, isEdited, updatedAt }) => {
      if (!commentId) return;

      setComments((prev = []) =>
        prev.map((c) =>
          String(c.commentId) === String(commentId)
            ? {
                ...c,
                text,
                isEdited: Boolean(isEdited),
                updatedAt,
              }
            : c
        )
      );
    },
    [setComments]
  );

  /* =========================================================
     🚩 REPORT SUCCESS (LISTENER)
  ========================================================= */
  const handleReportSuccess = useCallback(
    ({ type, targetId }) => {
      if (!type || !targetId) return;

      if (type === "post") {
        updateAllFeeds(
          (prev = []) =>
            prev.map((p) =>
              String(p.postId) === String(targetId)
                ? {
                    ...p,
                    isReported: true,
                    reportsCount: (p.reportsCount || 0) + 1,
                  }
                : p
            ),
          feedSetters
        );
      }

      if (type === "comment") {
        setComments((prev = []) =>
          prev.map((c) =>
            String(c.commentId) === String(targetId)
              ? {
                  ...c,
                  isReported: true,
                  reportsCount: (c.reportsCount || 0) + 1,
                }
              : c
          )
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setComments, setFeedPosts, setGlobalLivePosts, setInteractionPosts]
  );

  /* =========================================================
     ⚠️ FEED ERROR (LISTENER)
  ========================================================= */
  const handleFeedError = useCallback(
    (payload = {}) => {
      const { message } = payload;
      console.error("⚠️ feed-error:", message || payload);
      setFeedError?.(message || "Something went wrong");
    },
    [setFeedError]
  );

  /* =========================================================
     🎧 REGISTER / UNREGISTER LISTENERS
  ========================================================= */
  useEffect(() => {
    if (!socket) return;

    socket.on("feed:post-deleted", handlePostDeleted);
    socket.on("feed:post-edited", handlePostEdited);
    socket.on("feed:comment-deleted", handleCommentDeleted);
    socket.on("feed:comment-edited", handleCommentEdited);
    socket.on("feed-report-success", handleReportSuccess);
    socket.on("feed-error", handleFeedError);

    return () => {
      socket.off("feed:post-deleted", handlePostDeleted);
      socket.off("feed:post-edited", handlePostEdited);
      socket.off("feed:comment-deleted", handleCommentDeleted);
      socket.off("feed:comment-edited", handleCommentEdited);
      socket.off("feed-report-success", handleReportSuccess);
      socket.off("feed-error", handleFeedError);
    };
  }, [
    socket,
    handlePostDeleted,
    handlePostEdited,
    handleCommentDeleted,
    handleCommentEdited,
    handleReportSuccess,
    handleFeedError,
  ]);

  return {
    deletePost,
    deleteComment,
    editPost,
    editComment,
    reportPost,
    reportComment,
  };
}